/**
 * @module app/components/Performance
 */
import _ from 'lodash';
import MessageBusTopics from '../../common/MessageBusTopics';

/**
 * Is global performance with all measurement functions supported.
 * Required for creating mark/measures for further analysis
 *
 * @type {Boolean}
 */
var performanceSupported = window.performance && window.performance.mark &&
    window.performance.getEntriesByName && window.performance.now;

/**
 * Save metric to new relic
 *
 * @param {String} action key for the metric
 * @param {Object} data data to be passed
 *
 * @returns {void}
 *
 * @priavte
 */
var addNewRelicAction = function addNewRelicAction(action, data) {

    if (window.newrelic) {

        window.newrelic.addPageAction(action, data);
    }
};

/**
 * Extract duration time from performance object
 *
 * @param {Object} measure
 *
 * @returns {Number}
 *
 * @private
 */
var readDuration = function readDuration(measure) {

    if (measure && measure[0] && measure[0].duration) {

        return measure[0].duration;
    } else {

        return -1;
    }
};

/**
 * Perform performance measure and save result to new relic
 *
 * @params {String} key of a metric
 *
 * @returns {Number} duration since connectStart
 */
var measure = function measure(metricKey, startMark, endMark) {

    startMark = startMark || 'connectStart';

    if (!endMark) {
        endMark = metricKey + 'Mark';
        window.performance.mark(endMark);
    }

    window.performance.measure(metricKey + 'Measure', startMark, endMark);

    var duration = readDuration(window.performance.getEntriesByName(metricKey + 'Measure'));

    if (duration > -1) {

        addNewRelicAction(metricKey, {
            time: duration,
        });
    }

    return duration;
};

/**
 * Create performance mark and measure for time to first bet metric
 *
 * @returns {Number} Time of ms from initial load with fallback to performance.now
 *
 * @private
 */
var makeTTFBetMark = function makeTTFBetMark() {

    if (!performanceSupported) {

        return window.performance.now();
    }

    return measure('ttfbet');
};

/**
 * Create performance mark and measure time until betslip is loaded and emitted its bets
 *
 * @returns {void}
 *
 * @private
 */
var measureBetslipReady = function measureBetslipReady() {

    if (!performanceSupported) {

        return;
    }

    measure('betslipReady');
};

/**
 * Create performance mark and measure time until betslip is loaded and emitted its bets
 *
 * @param {Object} e - message bus event
 * @param {Object} value - the specs published by sidebar navigation
 *
 * @returns {void}
 *
 * @private
 */
var measureSidebarNavigationReady = function measureSidebarNavigationReady(e, value) {
    if (!performanceSupported) {

        return window.performance.now();
    }

    return measure('sidebarNavigationReady', 'navigationStart', value.mark);
};

var reportOverlayWarningIndex = function reportOverlayWarningIndex(event, value) {
    if (!value || !value.displayed || !event) {
        return;
    }

    addNewRelicAction(event,
        { displayed: value.displayed });
    return;
};


/**
 * @constructor
 */
var Performance = function Performance() {
};

Performance.prototype = {

    /**
     * Make sure calling performance.now doesn't break anything.
     *
     * @returns {void}
     * @private
     */
    polyfill: function polyfillPerformance() {

        if (!(window.performance && window.performance.now)) {

            window.performance = {};
            window.performance.now = function () {
                return -1;
            };
            window.performance.timing = {
                navigationStart: 0,
                domComplete: 0,
                requestStart: 0,
                responseEnd: 0,
            };
        }
    },

    /**
     * Publish the time to first bet.
     *
     * @returns {void}
     * @public
     */
    publishTimeToFirstBet: function publishTimeToFirstBet() {

        if (window.WH && window.WH.messageBus) {

            var mark = makeTTFBetMark();
            window.WH.messageBus.publishSticky(MessageBusTopics.TTF_BET_DETERMINED, mark);
        }
    },

    /**
     * Publish the network time metric.
     *
     * @returns {void}
     * @public
     */
    publishNetworkTime: function publishNetworkTime() {

        if (window.WH && window.WH.messageBus) {
            var networkTime = window.performance.timing.responseEnd - window.performance.timing.requestStart;
            window.WH.messageBus.publishSticky(MessageBusTopics.NETWORK_TIME_DETERMINED, networkTime);
        }
    },

    /**
     * Publish the DOM ready metric.
     *
     * @returns {void}
     * @public
     */
    publishDomReady: function publishDomReady() {

        if (window.WH && window.WH.messageBus && window.performance.timing) {
            var domReady = window.performance.timing.domComplete - window.performance.timing.navigationStart;
            window.WH.messageBus.publishSticky(MessageBusTopics.DOM_READY_DETERMINED, domReady);
        }
    },
};
/*jshint -W079 */
let performance = new Performance();
performance.polyfill();

window.WH.messageBus.subscribe(MessageBusTopics.BETSLIP_ADAPTER_GET_BETS, _.once(measureBetslipReady));
window.WH.messageBus.subscribe(MessageBusTopics.SIDEBAR_NAVIGATION_READY, _.once(measureSidebarNavigationReady));
window.WH.messageBus.subscribe(MessageBusTopics.WARNING_DISPLAYED, reportOverlayWarningIndex);


export default {
    publishTimings: function () {
        performance.publishTimeToFirstBet();
        performance.publishNetworkTime();
        performance.publishDomReady();
    },
};
